/**
 * Сборщик: лента заданий, бронь, сирена, «Забрал»/«Вернул», отмена, заработок.
 *
 * Только `/v1/collector/*` — `/v1/scout/*` сборщику отвечает 403, и ни один
 * хук отсюда туда не ходит (сторожит `app/collector.test.tsx`).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ⚠ «ЗАБРАЛ» И «ВЕРНУЛ» ИДУТ МИМО ОЧЕРЕДИ — ТОЛЬКО ОНЛАЙН
 * ─────────────────────────────────────────────────────────────────────────
 * Правило CLAUDE.md «всё через outbox» здесь нарушено сознательно, по тем же
 * причинам, что у команд замку (`api/device.ts`) и «Забрать» пула:
 *  1. «Забрал» ОТКРЫВАЕТ ЗАМОК. Досланный через полчаса, он открыл бы велик,
 *     у которого никого нет.
 *  2. Очередь повторяет любой 5xx, а UNLOCK_FAILED/LOCK_FAILED — это 503:
 *     запись крутилась бы вечно и держала всю очередь за собой.
 *  3. NOT_IN_HUB, TOO_FAR, QR_MISMATCH — ответ нужен, пока сборщик стоит у
 *     велика: переставить на HUB, подойти ближе, сканировать другой код.
 *  4. Перед действием фото должно лечь в хранилище — это тоже сеть.
 * Офлайн-очередь «Забрал/Вернул» в ТЗ — этап 3. «Отменить» — факт, а не
 * команда замку, поэтому он идёт через очередь (`collector_cancel`) — но
 * только для брони. Отмена «в машине» закрывает замок (503 LOCK_FAILED, если
 * не закрылся), и по тем же причинам, что «Вернул», она только онлайн
 * (`cancelPicked`, финальное ревью волны 3, I-1).
 */
import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { ApiError, NetworkError, RequestTimeoutError, request } from '@/lib/http'
import type { LatLng } from '@/lib/geo'
import { enqueue } from '@/lib/offline/outbox'
import { PhotoDecodeError, PhotoUploadError, putPhoto } from '@/lib/photo'
import {
  LOCK_WAIT_TIMEOUT_MS,
  cancelBody,
  isBlockedError,
  pickBody,
  returnBody,
  type CancelReason,
  type ReturnResult,
} from '@/lib/collector'
import type { TKey } from '@/lib/collector-i18n'
import { nestedObject, optionalNested } from './nested'

/**
 * Целое с провода: число, строка (int64) или `null` (незаданное в
 * grpc-gateway). Контракт обещает int, но int64 строкой не должен ронять
 * ленту — `zDouble0` в `api/hubs.ts`, тот же урок.
 */
const zNum0 = z
  .union([z.number(), z.string(), z.null()])
  .default(0)
  .transform((v) => {
    const n = typeof v === 'string' ? Number(v) : (v ?? 0)
    return Number.isFinite(n) ? n : 0
  })

const hubRefSchema = nestedObject({
  id: z.string().default(''),
  name: z.string().default(''),
  lat: zNum0,
  lng: zNum0,
})

/**
 * `state`/`band`/`status` — строки, не enum: «Вернул» отвечает
 * `state: "returned"`, которого в списке состояний ленты нет, и новое
 * значение на сервере не должно ронять разбор (`lib/collector.taskStage`).
 */
export const collectorTaskSchema = z.object({
  vehicleId: z.string().default(''),
  number: z.string().default(''),
  lat: zNum0,
  lng: zNum0,
  /** -1 — неизвестно; опущенное — 0 (protojson не шлёт нули). */
  batteryPct: zNum0,
  /** available | faulty */
  status: z.string().default(''),
  /** Метры за границей зоны катания. */
  outsideM: zNum0,
  /** near | far */
  band: z.string().default(''),
  rateKzt: zNum0,
  /** open | reserved | picked (| returned в ответе «Вернул») */
  state: z.string().default(''),
  reservedUntil: z.string().default(''),
  pickedAt: z.string().default(''),
  nearestHub: hubRefSchema,
})
export type CollectorTask = z.infer<typeof collectorTaskSchema>

const collectorHubSchema = z.object({
  id: z.string().default(''),
  name: z.string().default(''),
  polygonWkt: z.string().default(''),
  lat: zNum0,
  lng: zNum0,
})
export type CollectorHub = z.infer<typeof collectorHubSchema>

export const collectorTasksReplySchema = z.object({
  tasks: z.array(collectorTaskSchema).default([]),
  hubs: z.array(collectorHubSchema).default([]),
  limits: nestedObject({
      maxReservations: zNum0,
      reservationMinutes: zNum0,
      /** indriver | night (волна 4); пусто — старый сервер, читается как дневной. */
      kind: z.string().default(''),
      nightStart: z.string().default(''),
      nightEnd: z.string().default(''),
      /** true у дневного; опущенное false значит «закрыто» только вместе с kind=night. */
      windowOpen: z.boolean().default(false),
      /** 0 — для этого вида прогрессии нет. */
      progressionFrom: zNum0,
      progressionBonusKzt: zNum0,
    }),
})
export type CollectorTasksReply = z.infer<typeof collectorTasksReplySchema>

export const returnReplySchema = z.object({
  task: optionalNested(collectorTaskSchema),
  rateKzt: zNum0,
  hubName: z.string().default(''),
})

export const earningsReplySchema = z.object({
  today: nestedObject({ returns: zNum0, amountKzt: zNum0 }),
  profile: nestedObject({ kind: z.string().default(''), blocked: z.boolean().default(false) }),
  /** Текущая смена: дневная — сутки по Алматы, ночная — окно от последнего nightStart. */
  shift: nestedObject({
    returns: zNum0,
    amountKzt: zNum0,
    bonusKzt: zNum0,
    startedAt: z.string().default(''),
    endsAt: z.string().default(''),
  }),
  progressionFrom: zNum0,
  progressionBonusKzt: zNum0,
  /** Свежие сверху, дни без возвратов сервер не шлёт (k0) — показываем как есть. */
  days: z
    .array(
      z.object({
        /** YYYY-MM-DD по Алматы. */
        date: z.string().default(''),
        returns: zNum0,
        /** = начислено + спорное + выплачено (отклонённые не считаются). */
        amountKzt: zNum0,
        bonusKzt: zNum0,
        accruedKzt: zNum0,
        disputedKzt: zNum0,
        paidKzt: zNum0,
      }),
    )
    .default([]),
  recent: z
    .array(
      z.object({
        vehicleNumber: z.string().default(''),
        rateKzt: zNum0,
        /** whole | broken */
        result: z.string().default(''),
        returnedAt: z.string().default(''),
        hubName: z.string().default(''),
        bonusKzt: zNum0,
        /** accrued | disputed | rejected | paid; строка — незнакомое не роняет разбор. */
        payoutStatus: z.string().default(''),
        /** same_bike_repeat | anomalous_shift | ride_after_return | manual */
        disputeReasons: z.array(z.string()).default([]),
      }),
    )
    .default([]),
})
export type Earnings = z.infer<typeof earningsReplySchema>

const uploadUrlReply = z.object({
  uploadUrl: z.string().default(''),
  photoKey: z.string().default(''),
})

export const CQK = {
  tasks: ['collector.tasks'] as const,
  earnings: ['collector.earnings'] as const,
}

/** Ленте 30 с (ТЗ: «обновление 30 с»). */
const REFRESH_MS = 30_000

/** 404 — маршрута на сервере нет (бэкенд сборщика не выкачен). */
export function isCollectorUnavailable(e: unknown): boolean {
  return e instanceof ApiError && e.status === 404
}

/**
 * Все reason из контракта — у каждого свой текст в `collector-i18n`.
 * POSITION_REQUIRED и COLLECTOR_TASK_STATE — из proto волны 3 (отчёт k0),
 * DEVICE_OFFLINE и BIKE_LOCK_UNAVAILABLE — 503 fleet (финальное ревью, I-2);
 * в api-contract.md их нет. RESULT/CANCEL_REASON_INVALID сюда не входят:
 * их даёт только битое тело, а не человек — пусть видно текст сервера.
 */
export const COLLECTOR_REASONS = [
  'COLLECTOR_TASK_TAKEN',
  'COLLECTOR_LIMIT',
  'COLLECTOR_NOT_ELIGIBLE',
  'COLLECTOR_NOT_HOLDER',
  'QR_MISMATCH',
  'TOO_FAR',
  'PHOTO_REQUIRED',
  'UNLOCK_FAILED',
  'NOT_IN_HUB',
  'RETURN_TOO_SOON',
  'LOCK_FAILED',
  'POSITION_REQUIRED',
  'COLLECTOR_TASK_STATE',
  'DEVICE_OFFLINE',
  'BIKE_LOCK_UNAVAILABLE',
  'COLLECTOR_OUTSIDE_WINDOW',
  'COLLECTOR_BLOCKED',
] as const

const KNOWN_REASONS = new Set<string>(COLLECTOR_REASONS)

/**
 * Ключ человеческого текста ошибки. По `reason`, не по тексту — правило
 * CLAUDE.md. `null` — отказ незнакомый: экран покажет текст сервера, чтобы
 * новая причина не превратилась в молчаливое «не удалось».
 *
 * `step` — какое действие отказано; один reason значит разное:
 *  - 409 COLLECTOR_TASK_STATE на «Забрал» — первый «Забрал» ещё открывает
 *    замок (сервер ждёт до ~2 мин, окно до ~3,7 мин): «подождите»;
 *  - DEVICE_OFFLINE на «Забрал» — выбрать другой велик, на «Вернул» — ждать:
 *    велик уже в руках;
 *  - LOCK_FAILED/DEVICE_OFFLINE на отмене «в машине» — повторить или
 *    вернуть на HUB.
 */
export type ErrorStep = StepKind | 'cancel'

export function collectorErrorKey(e: unknown, step?: ErrorStep): TKey | null {
  if (e instanceof RequestTimeoutError) return 'err_timeout'
  if (e instanceof NetworkError) return 'err_network'
  if (e instanceof PhotoUploadError) return 'err_photo_upload'
  if (e instanceof PhotoDecodeError) return 'err_photo_decode'
  if (e instanceof ApiError) {
    if (step === 'pick' && e.reason === 'COLLECTOR_TASK_STATE') return 'err_pick_in_flight'
    if (step === 'return' && e.reason === 'DEVICE_OFFLINE') return 'err_return_device_offline'
    if (step === 'cancel' && (e.reason === 'LOCK_FAILED' || e.reason === 'DEVICE_OFFLINE')) {
      return 'err_cancel_lock_failed'
    }
    if (KNOWN_REASONS.has(e.reason)) return `err_${e.reason}` as TKey
    if (e.status === 404) return 'not_deployed'
    if (e.status === 403) return 'err_forbidden'
    return null
  }
  return 'err_unknown'
}

export function collectorErrorText(
  e: unknown,
  t: (key: TKey) => string,
  step?: ErrorStep,
): string {
  const key = collectorErrorKey(e, step)
  if (key) return t(key)
  return e instanceof Error && e.message ? e.message : t('err_unknown')
}

/**
 * Лента заданий.
 *
 * ⚠ ПОЗИЦИЯ НЕ В КЛЮЧЕ ЗАПРОСА. `watchPosition` присылает её каждые
 * несколько секунд, и ключ с координатами заводил бы новый запрос на каждое
 * движение. Опрос берёт последнюю точку из ref.
 *
 * Геопозиция приходит позже первого кадра: запрос, ушедший без неё,
 * повторяется один раз, как только она появилась, — не ждать 30 с опроса
 * с лентой, не отсортированной по расстоянию.
 *
 * После 404 опрос молчит — как `useMyPool`: пока бэкенд не выкачен, иначе
 * каждый сборщик слал бы 404 раз в 30 с.
 */
export function useCollectorTasks(me: LatLng | null) {
  const meRef = useRef(me)
  meRef.current = me
  const sentBlind = useRef(false)
  const query = useQuery({
    queryKey: CQK.tasks,
    queryFn: () => {
      const at = meRef.current
      sentBlind.current = at === null
      return request('/v1/collector/tasks', collectorTasksReplySchema, {
        query: at ? { lat: at.lat, lng: at.lng } : {},
      })
    },
    staleTime: REFRESH_MS,
    // 403 COLLECTOR_BLOCKED повтором не лечится — экран блокировки без лишней секунды ожидания.
    retry: (failures, e) => !isCollectorUnavailable(e) && !isBlockedError(e) && failures < 1,
    refetchInterval: (q: { state: { error: unknown } }) =>
      isCollectorUnavailable(q.state.error) ? false : REFRESH_MS,
  })
  const located = me !== null
  const { refetch } = query
  useEffect(() => {
    if (located && sentBlind.current) void refetch()
  }, [located, refetch])
  return query
}

/** Заменить задание в ленте ответом сервера — без второго запроса. */
function patchTask(old: CollectorTasksReply | undefined, task: CollectorTask) {
  if (!old) return old
  return { ...old, tasks: old.tasks.map((x) => (x.vehicleId === task.vehicleId ? task : x)) }
}

/** «Беру» — вопрос серверу (занято? лимит?), ответ нужен сейчас: онлайн. */
export function useReserveTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vehicleId: string) =>
      request(`/v1/collector/tasks/${vehicleId}/reserve`, collectorTaskSchema, {
        method: 'POST',
        body: {},
      }),
    onSuccess: (task) => {
      qc.setQueryData<CollectorTasksReply>(CQK.tasks, (old) => patchTask(old, task))
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: CQK.tasks })
    },
  })
}

/** Сирена — живая радиокоманда, как у скаута (`api/device.ts`): мимо очереди. */
export function useCollectorSiren() {
  return useMutation({
    mutationFn: (vehicleId: string) =>
      request(`/v1/collector/tasks/${vehicleId}/siren`, z.unknown(), {
        method: 'POST',
        body: {},
      }),
  })
}

/** «Отменить» бронь — через очередь (см. шапку). Ленту перечитает досылка. */
export function useCancelTask() {
  return useMutation({
    mutationFn: ({
      vehicleId,
      reason,
      comment,
    }: {
      vehicleId: string
      reason: CancelReason
      comment: string
    }) => enqueue('collector_cancel', vehicleId, cancelBody(reason, comment)),
  })
}

/**
 * «Отменить» велик в машине — онлайн, ждёт замок, как «Вернул»: в очереди
 * 503 LOCK_FAILED крутился бы вечно, а «Вернул» был бы заперт за ним.
 */
export function cancelPicked(args: {
  vehicleId: string
  reason: CancelReason
  comment: string
  attemptKey: string
}): Promise<unknown> {
  return request(`/v1/collector/tasks/${args.vehicleId}/cancel`, z.unknown(), {
    method: 'POST',
    body: cancelBody(args.reason, args.comment),
    idempotencyKey: args.attemptKey,
    timeoutMs: LOCK_WAIT_TIMEOUT_MS,
  })
}

export function useCancelPicked() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: cancelPicked,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CQK.tasks })
    },
  })
}

export function useCollectorEarnings() {
  return useQuery({
    queryKey: CQK.earnings,
    queryFn: () => request('/v1/collector/earnings', earningsReplySchema),
    staleTime: 60_000,
    // Блокировка, выданная посреди смены, должна дойти до экранов: заработок открыт и заблокированному.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: (failures, e) => !isCollectorUnavailable(e) && failures < 1,
  })
}

/**
 * Доступ приостановлен: источник правды — `earnings.profile.blocked` (лента
 * заблокированному отвечает 200 только с его взятыми заданиями). 403
 * COLLECTOR_BLOCKED с ленты тоже считается — так отвечает старый сервер.
 */
export function useCollectorBlocked(tasksError: unknown): boolean {
  const earnings = useCollectorEarnings()
  return earnings.data?.profile.blocked === true || isBlockedError(tasksError)
}

/**
 * Сохранить ли ключ идемпотентности для следующей попытки.
 *
 * Обрыв связи — запрос мог дойти и открыть замок; повтор с тем же ключом
 * сервер узнает. Ответ сервера (QR_MISMATCH, UNLOCK_FAILED…) — попытка
 * закончена, следующая — новая, иначе повтор получил бы тот же отказ из
 * кэша идемпотентности. Фото к ключу не относится: его попытка своя.
 */
export function keepAttemptKey(e: unknown): boolean {
  return !(e instanceof ApiError)
}

export type StepKind = 'pick' | 'return'

export type StepArgs = {
  kind: StepKind
  vehicleId: string
  qrToken: string
  me: LatLng
  photo: Blob
  /** Уже загруженное фото этой попытки; пусто — грузить. */
  photoKey: string
  attemptKey: string
  result?: ReturnResult
  /**
   * Фото легло в хранилище. Зовётся СРАЗУ, а не с успехом действия: после
   * QR_MISMATCH снимок уже на сервере, и следующая попытка его не грузит.
   */
  onUploaded?: (photoKey: string) => void
}

/**
 * «Забрал»/«Вернул»: фото в хранилище, потом действие.
 *
 * Фото грузится ДО действия (сервер требует `photoKey`, 400 PHOTO_REQUIRED) и
 * один раз на снимок: после QR_MISMATCH сборщик пересканирует код, а не
 * переснимает и не ждёт повторной загрузки. Ключ фото возвращается наверх —
 * экран держит его до смены снимка.
 */
export async function runStep(
  args: StepArgs,
): Promise<{ photoKey: string; reply: { rateKzt: number; hubName: string } }> {
  let photoKey = args.photoKey
  if (photoKey === '') {
    const issued = await request('/v1/collector/photo-upload-url', uploadUrlReply, {
      method: 'POST',
      body: { vehicleId: args.vehicleId, step: args.kind },
    })
    await putPhoto(issued.uploadUrl, args.photo)
    photoKey = issued.photoKey
    args.onUploaded?.(photoKey)
  }

  const base = { qrToken: args.qrToken, me: args.me, photoKey }
  const path = `/v1/collector/tasks/${args.vehicleId}/${args.kind}`
  if (args.kind === 'pick') {
    const task = await request(path, collectorTaskSchema, {
      method: 'POST',
      body: pickBody(base),
      idempotencyKey: args.attemptKey,
      timeoutMs: LOCK_WAIT_TIMEOUT_MS,
    })
    return { photoKey, reply: { rateKzt: task.rateKzt, hubName: task.nearestHub.name } }
  }
  const reply = await request(path, returnReplySchema, {
    method: 'POST',
    body: returnBody({ ...base, result: args.result ?? 'whole' }),
    idempotencyKey: args.attemptKey,
    timeoutMs: LOCK_WAIT_TIMEOUT_MS,
  })
  return { photoKey, reply }
}
